import { isFulfilled } from "@reduxjs/toolkit";
import { differenceInMinutes, isValid, parseISO } from "date-fns";
import { useState } from "react";
import { TSuccessMsg } from ".";
import DefaultImg from "../../../assets/img/default-image.svg";
import { CANDIDATE_TIMESHEET_PENDING_APPROVAL } from "../../../constants/app";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import { hideTransparentPreloaderAction, showTransparentPreloaderAction } from "../../../store/application/slice";
import { updateTimesheetStatusById } from "../../../store/timesheet/thunks";
import { ITimesheetData } from "../../../store/timesheet/types";
import CustomButton from "../../UI/Buttons/CustomButton";
import LeftArrow from "../../UI/Icons/LeftArrow";
import RoundedTick from "../../UI/Icons/RoundedTick";
import StarRating from "../../UI/StarRating";

export function calculateBreakDuration(
  start: string | null,
  end: string | null
) {
  if (!start || !end) return "--";
  const startTime = parseISO(start);
  const endTime = parseISO(end);

  if (!isValid(startTime) || !isValid(endTime)) return "--";

  const diff = differenceInMinutes(endTime, startTime);
  if (diff <= 0) return "0min";

  const hrs = Math.floor(diff / 60);
  const mins = diff % 60;

  return hrs > 0 ? `${hrs}hr ${mins}min` : `${mins}min`;
}

const ReviewTimesheet = ({
  timesheet,
  onActionSuccess,
  onClickClose,
  type,
  count,
  currentIndex
}: {
  timesheet: ITimesheetData;
  onActionSuccess: (msg: TSuccessMsg) => void;
  onClickClose: () => void;
  type?: string;
  count?: number;
  currentIndex?: number;
}) => {
  const dispatch = useDispatch();

  const InProgress = useSelector((state) => state.timesheet.inProgress);

  const [isApproved, setIsApproved] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  const [comment, setComment] = useState("");

  const currentTimesheetIndex = currentIndex ? currentIndex : 0;

  const handleApproval = async () => {
    try {
      dispatch(showTransparentPreloaderAction())
      const result = await dispatch(
        updateTimesheetStatusById({ id: timesheet.id, status: "approve" })
      );
      if (isFulfilled(result)) {
        onActionSuccess("Submitted");
      }
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(hideTransparentPreloaderAction())
      onClickClose();
    }
  };

  const handleDecline = async () => {
    try {
      const result = await dispatch(
        updateTimesheetStatusById({
          id: timesheet.id,
          status: "decline",
          message: comment,
        })
      );
      if (isFulfilled(result)) {
        onActionSuccess("Done")
      }
    } catch (e) {
      console.error(e);
    } finally {
      onClickClose();
    }
  };


  return (
    
      <div className="d-flex flex-column gap-15">
        { type === 'index' ? 
          <div className="pr-25px d-flex gap-20 align-items-center justify-content-between">
            <div className="fs-22 fw-400 text-black">Review Timesheet</div>
            <div className="fs-16 fw-400 text-grey-dark">
              {currentTimesheetIndex + 1} of {count}
            </div>
          </div> : 
          <div className="fs-22 fw-400 text-black">Review Timesheet</div>
        }
        <div className="bg-off-white h-1px"></div>

        <div className="d-flex flex-column gap-15 mobile-job-scroll">
          {/* Candidate Info */}
          <div className="d-flex gap-20">
            <div className="w-50px h-50px border-2 border-grey-dark radius-40px">
              <img
                src={timesheet?.candidate?.photo || DefaultImg}
                alt="candidate"
                width={50}
                height={50}
                className="img-responsive rounded-circle object-fit-cover"
              />
            </div>
            <div className="flex-grow-1 d-flex flex-column gap-20">
              <div className="flex-grow-1 d-flex flex-column gap-3 text-break">
                <div className="fs-16 fw-700 text-black">
                  {timesheet?.candidate?.first_name}{" "}
                  {timesheet?.candidate?.last_name}
                </div>
                <StarRating
                  rating={
                    timesheet?.rating?.latest_rating ||
                    timesheet?.rating?.average_rating ||
                    0
                  }
                  // color={
                  //   !timesheet?.rating?.latest_rating ? "#aaaaaa" : "#51C2B9"
                  // }
                  color={"#51C2B9"}
                />
                {!timesheet?.rating?.latest_rating &&
                  timesheet?.rating?.average_rating && (
                    <div className="fs-10 fw-400 text-grey-dark">Average Rating</div>
                )}
              </div>
            </div>
          </div>

          {/* Centre Info */}
          <div className="d-flex gap-20">
            <div className="w-50px h-50px border-2 border-grey-dark radius-40px">
              <img
                src={timesheet?.centre?.photo || DefaultImg}
                alt="centre-photo"
                width={50}
                height={50}
                className="img-responsive rounded-circle object-fit-cover"
              />
            </div>
            <div className="flex-grow-1 d-flex flex-column gap-10">
              <div className="flex-grow-1 d-flex flex-column gap-3 text-break">
                <div className="fs-16 fw-700 text-black">
                  {timesheet?.centre?.business_name},{" "}
                  <span className="fw-400">{timesheet?.centre?.address}</span>
                </div>
                <div className="fs-13 fw-400 text-black">
                  {timesheet?.vacancy?.matching_position?.name ||
                    timesheet?.position_name}
                </div>
              </div>
              <div className="d-flex flex-column gap-3 fs-13 text-black">
                <div className="fw-700">Job Timeframe</div>
                <div className="fw-400">{timesheet?.timeframe}</div>
              </div>
            </div>
          </div>

          <div className="h-1px"></div>

          {/* Review Summary */}
          {timesheet?.decline_reason?.message && (
            <>
              <div className="d-flex flex-column gap-5">
                <div className="fs-16 fw-700 text-black">Your Comment:</div>
                <div className="fs-16 fw-400 text-black">
                  {timesheet.decline_reason.message}
                </div>
              </div>
              <div className="h-1px"></div>
            </>
          )}
          {
            !timesheet?.did_not_work && (
              <>
                <div className="fs-16 fw-700 text-black">
                  Review the details for the above job:
                </div>
                <div className="d-flex gap-50">
                  <div className="d-flex flex-column gap-5 fw-400">
                    <div className="fs-13 text-grey-medium">Start time</div>
                    <div className="fs-16 text-black">{timesheet.job_start_time}</div>
                  </div>
                  <div className="d-flex flex-column gap-5 fw-400">
                    <div className="fs-13 text-grey-medium">End time</div>
                    <div className="fs-16 text-black">{timesheet.job_end_time}</div>
                  </div>
                  <div className="d-flex flex-column gap-5 fw-400">
                    <div className="fs-13 text-grey-medium">Break</div>
                    <div className="fs-16 text-black">
                      {calculateBreakDuration(
                        timesheet.break_start_time,
                        timesheet.break_end_time
                      )}
                    </div>
                  </div>
                </div>
              </>
            )
          }

          {
            timesheet?.did_not_work && (
             <>
               <div className="fs-16 fw-700 text-black">
                Timesheet details of the above job:
              </div>
              <div className="fs-16">They did not work on this day</div>
             </>
            )
          }

          <div className="h-1px"></div>

          {/* Conditional Action Section */}
          {timesheet.status_id === CANDIDATE_TIMESHEET_PENDING_APPROVAL && (
            <>
              {!isDeclined ? (
                <>
                  {/* Approval Checkbox */}
                  <div className="border border-grey-light radius-8px py-10px px-15px d-flex flex-column gap-8">
                    <div className="theme-check">
                      <input
                        type="checkbox"
                        className="theme-check-input"
                        id="approve_check"
                        checked={isApproved}
                        onChange={(e) => setIsApproved(e.target.checked)}
                      />
                      <label
                        htmlFor="approve_check"
                        className="theme-check-label fs-16 fw-700 text-black"
                      >
                        I approve this timesheet for payroll.
                      </label>
                    </div>
                    <div className="fs-13 fw-400 text-black pl-34px">
                      It is important to check your employees' timesheets are
                      correct. Once submitted for payroll processing, the
                      timesheets are locked and cannot be edited.
                    </div>
                  </div>
                 

                  {/* Action Buttons */}
                  <div className="d-flex flex-column flex-lg-row gap-20 justify-content-lg-between">
                    <CustomButton
                      variant="primary"
                      type="button"
                      className="btn-theme bg-alert text-white fw-700 mobile-w-100"
                      onClick={() => setIsDeclined(true)}
                    >
                      Decline
                    </CustomButton>

                    <CustomButton
                      variant="primary"
                      type="button"
                      className="btn-theme bg-teal text-black mobile-w-100"
                      disabled={!isApproved}
                      isLoading={InProgress}
                      onClick={handleApproval}
                    >
                      <RoundedTick
                        color={InProgress || !isApproved ? "#aaaaaa" : "black"}
                      />
                      Submit For Payroll
                    </CustomButton>
                  </div>
                </>
              ) : (
                <>
                  {/* Comment Box */}
                  <div className="fs-16 fw-700 text-black">
                    Include a comment on what the employee should revise:
                  </div>
                  <div className="form-group">
                    <textarea
                      className="form-control py-10px"
                      placeholder="Enter comment..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                  </div>

                  {/* Decline Action Buttons */}
                  <div className="d-flex flex-column flex-lg-row gap-20 justify-content-lg-between">
                    <CustomButton
                      variant="primary"
                      type="button"
                      className="btn-theme bg-off-white text-black mobile-w-100"
                      onClick={() => {
                        setIsDeclined(false);
                        setComment("");
                      }}
                    >
                      <LeftArrow /> Back to Approval
                    </CustomButton>

                    <CustomButton
                      variant="primary"
                      type="button"
                      className="btn-theme bg-alert text-white fw-700 mobile-w-100"
                      onClick={handleDecline}
                      isLoading={InProgress}
                      disabled={comment.trim().length < 10 || InProgress}
                    >
                      Decline and Notify
                    </CustomButton>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
   
  );
};

export default ReviewTimesheet;
